import * as React from 'react';
import './Style.scss';
import Tree from 'rc-tree';
import { Form, ProgressBar } from 'react-bootstrap';
import { AutoSizer, List } from 'react-virtualized';
import { toast } from 'react-toastify';
import { getLocalization } from 'global/global';
import { useAppDispatch, useAppSelector } from 'index';
import { AdminLocationHierarchy, LocationNode } from 'Interfaces/Admin';
import { fetchLocations } from 'api/adminApi';
import { loadedHierarchyLocations } from 'reducers/admin/meta';
import { applySameLevelRule, buildLocationTree, levelsByKey } from './locationTreeUtils';

interface Props {
  hierarchy: AdminLocationHierarchy;
  selectedKeys: string[];
  onChange: (keys: string[], titles: string[]) => void;
}

const titlesFor = (roots: LocationNode[], keys: string[]): string[] => {
  const titles: string[] = [];
  const walk = (nodes: LocationNode[]) => nodes.forEach((node) => {
    if (keys.includes(node.key)) {
      titles.push(node.title);
    }
    if (node.children) {
      walk(node.children);
    }
  });
  walk(roots);
  return titles;
};

/** Replaces the dynatree checkbox tree used for per-user location assignment. */
export default function LocationTree(props: Props) {
  const dispatch = useAppDispatch();
  const cached = useAppSelector(
    (state) => state.admin.meta.hierarchies.find((h) => h.id === props.hierarchy.id)?.locations,
  );
  const clientPersist = useAppSelector(state => state.clientPersist);
  const [loading, setLoading] = React.useState(false);
  const [treeData, setTreeData] = React.useState<LocationNode[]>([]);

  React.useEffect(() => {
    if (cached || loading) {
      return;
    }
    setLoading(true);
    fetchLocations(props.hierarchy.id)
      .then((nodes) => {
        dispatch(loadedHierarchyLocations({ hierarchyId: props.hierarchy.id, locations: nodes }));
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false));
  }, [props.hierarchy.id, cached]);

  const roots = cached || [];
  const levels = React.useMemo(() => levelsByKey(roots), [roots]);

  React.useEffect(() => {
    if (clientPersist.instance !== 'crm') {
      const tree = buildLocationTree(roots);
      setTreeData(tree);
    }
  }, [roots, clientPersist]);

  if (loading || !cached) {
    return (
      <div>
        <ProgressBar striped now={100} animated />
        <div>{getLocalization('loadingLocations')}</div>
      </div>
    );
  }

  const renderRow = ({index, key, style}) => {
    const { selectedKeys } = props;
    const location = roots[index];
    return (
      <div
        key={key} style={style}
        className="company-node"
      >
        <Form.Check
          id={`loc-${index}-${location.key}`}
          type="checkbox"
          label={location.title}
          checked={selectedKeys.includes(location.key)}
          onChange={(e) => {
            const keys = e.target.checked ? [...selectedKeys, location.key] :
              selectedKeys.filter(l => l !== location.key);
            props.onChange(keys, titlesFor(roots, keys));
          }}
        />
      </div>
    );
  };

  return clientPersist.instance === 'crm' ? (
    <AutoSizer disableHeight>
      {({width}) => (
        <List
          rowCount={roots.length}
          rowRenderer={renderRow}
          overscanRowCount={10}
          rowHeight={35}
          width={width}
          height={300}
          selectedLocations={props.selectedKeys}
        />
      )}
    </AutoSizer>
  ) : (
    <Tree
      checkable={true}
      // Levels are assigned independently, so a parent must not tick its children.
      checkStrictly
      selectable={false}
      defaultExpandedKeys={props.selectedKeys}
      checkedKeys={props.selectedKeys}
      treeData={treeData}
      className={'lhTreeContainer'}
      showLine={true}
      onCheck={(checked) => {
        const next = (Array.isArray(checked) ? checked : checked.checked).map(String);
        const { keys, rejected } = applySameLevelRule(props.selectedKeys, next, levels);
        if (rejected) {
          toast(getLocalization('selectSameLevelLocations'), {
            type: toast.TYPE.WARNING, autoClose: 3000, hideProgressBar: true,
          });
          return;
        }
        props.onChange(keys, titlesFor(roots, keys));
      }}
    />
  );
}
